import React from 'react';

import { Badge, Button, Icon } from '../../../components/common';
import type { AttendanceRecord, AttendanceStatus } from '../../attendance/types';
import { getAttendanceRequiredActorIdsForEvent, type RequiredActorContext } from '../../attendance/required-actors';
import type { ScheduleEvent } from '../../scheduler/types';
import { formatScheduleTimeForDisplay } from '../../scheduler/time-format';
import type { ToolId } from '../../layout/workspace-config';
import { getLocalDateKey } from '../logic/date-keys';
import { useDashboardAccessibleToolIds } from '../logic/useDashboardAccessibleToolIds';
import { useDashboardNavigation } from '../logic/useDashboardNavigation';
import { useAppStore } from '../../../store/useAppStore';
import { DashboardWidgetCard } from './DashboardWidgetCard';

interface DashboardActionItem {
    id: string;
    title: string;
    detail: string;
    sortKey: string;
    badge: string;
    badgeVariant: 'warning' | 'danger';
    toolId: ToolId;
    search?: string;
}

const RECORDED_ATTENDANCE_STATUSES = new Set<AttendanceStatus>([
    'present',
    'late',
    'excused',
    'unexcused',
    'absent',
]);

function getRequiredAttendanceActorIds(
    event: ScheduleEvent,
    context: RequiredActorContext
): Set<string> {
    return getAttendanceRequiredActorIdsForEvent(event, context);
}

function hasRecordedAttendanceStatus(status: AttendanceStatus | undefined): boolean {
    return Boolean(status && RECORDED_ATTENDANCE_STATUSES.has(status));
}

function needsAttendanceRecord(
    event: ScheduleEvent,
    attendanceRecordsByEventId: Map<string, AttendanceRecord>,
    actorContext: RequiredActorContext
): boolean {
    const requiredActorIds = getRequiredAttendanceActorIds(event, actorContext);

    if (requiredActorIds.size === 0) {
        return false;
    }

    const record = attendanceRecordsByEventId.get(event.id);
    if (!record) {
        return true;
    }

    for (const actorId of requiredActorIds) {
        if (!hasRecordedAttendanceStatus(record.actors[actorId]?.status)) {
            return true;
        }
    }

    return false;
}

function buildActionItems(
    events: ScheduleEvent[],
    attendanceRecords: AttendanceRecord[],
    actorContext: RequiredActorContext,
    today: string,
    accessibleToolIds: Set<ToolId>
): DashboardActionItem[] {
    const items: DashboardActionItem[] = [];
    const attendanceRecordsByEventId = new Map(attendanceRecords.map(record => [record.eventId, record]));

    for (const event of events) {
        if (
            event.date === today
            && accessibleToolIds.has('personnel')
            && needsAttendanceRecord(event, attendanceRecordsByEventId, actorContext)
        ) {
            items.push({
                id: `attendance-${event.id}`,
                title: `Record attendance: ${event.title}`,
                detail: formatScheduleTimeForDisplay(event.startTime),
                sortKey: `${event.date}\0${event.startTime}\0${event.title}`,
                badge: 'Attendance',
                badgeVariant: 'warning',
                toolId: 'personnel',
                search: `?subtab=attendance&eventId=${encodeURIComponent(event.id)}`,
            });
        }

        if (
            event.type === 'deadline'
            && event.status !== 'met'
            && event.date <= today
            && accessibleToolIds.has('scheduler')
        ) {
            items.push({
                id: `deadline-${event.id}`,
                title: event.title,
                detail: event.date,
                sortKey: `${event.date}\0${event.startTime}\0${event.title}`,
                badge: event.status === 'missed' ? 'Missed' : 'Due',
                badgeVariant: event.status === 'missed' ? 'danger' : 'warning',
                toolId: 'scheduler',
            });
        }
    }

    return items
        .toSorted((left, right) => left.sortKey.localeCompare(right.sortKey))
        .slice(0, 4);
}

export const ActionItemsWidget: React.FC = () => {
    const events = useAppStore(state => state.rehearsalSchedule || []);
    const attendanceRecords = useAppStore(state => state.attendanceRecords || []);
    const personnel = useAppStore(state => state.personnel || []);
    const showStructure = useAppStore(state => state.showStructure);
    const rehearsalScenes = useAppStore(state => state.rehearsalScenes || []);
    const castAssignments = useAppStore(state => state.castAssignments || []);
    const openTool = useDashboardNavigation();
    const accessibleToolIds = useDashboardAccessibleToolIds();
    const today = getLocalDateKey();
    const actorContext: RequiredActorContext = {
        actors: personnel.filter(member => member.category === 'cast' && !member.deleted),
        personnel,
        characters: showStructure.characters || [],
        acts: showStructure.acts || [],
        rehearsalScenes,
        castAssignments,
    };
    const items = buildActionItems(events, attendanceRecords, actorContext, today, accessibleToolIds);

    return (
        <DashboardWidgetCard title="Action Items" icon="task_alt" testId="action-items-widget">
            {items.length === 0 ? (
                <div className="text-center py-6">
                    <Icon name="check_circle" size="xl" className="text-emerald-400 dark:text-emerald-500 mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">No action items for today</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map(item => (
                        <div
                            key={item.id}
                            className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-3"
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <Badge variant={item.badgeVariant} size="sm">{item.badge}</Badge>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">{item.detail}</span>
                                </div>
                                <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {item.title}
                                </p>
                            </div>
                            <Button
                                aria-label={`Open ${item.title}`}
                                onClick={() => openTool(item.toolId, { search: item.search })}
                                size="sm"
                                variant="ghost"
                            >
                                Open
                            </Button>
                        </div>
                    ))}
                </div>
            )}
        </DashboardWidgetCard>
    );
};
